import React, { useEffect, useMemo, useState } from 'react';

// redux
import { useSelector, useDispatch } from 'react-redux';
import { selectSelectedTimeline } from '_redux/slices/timeline/timeline-areas';
import { selectAllEmployees, selectSendBroadcastMessageStatus, resetSendBroadcastMessageStatus, sendBroadcastMessage, setOpenBroadcastMessages } from '_redux/slices/timeline/employees';
import { selectFirebaseInfo, selectFirebaseUser } from '_redux/slices/main-screen/user';
import { selectUsedTheme } from '_redux/slices/timeline/theme';
import { setToast, setShowToast } from "_redux/slices/timeline/timeline-toast";

import { Col, Form, Row, Button } from 'react-bootstrap';

// select
import Select from "react-select";

// custom components
import { CustomSpinner } from "_atoms";
import CustomDivider from '_atoms/lines/custom-divider';

// icons
import { IoMdSend } from "react-icons/io";
import { FiRadio, FiUsers, FiUserCheck, FiMessageSquare, FiFileText, FiFilter } from "react-icons/fi";

// styles
import { Spacing, Colors } from '_styles';

// constants
import { timelineRoles } from '../timeline-constants';

const labelStyle = {
    fontSize: '14px',
    fontWeight: '500',
    color: 'grey',
    marginBottom: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
};

const roleFilterOptions = timelineRoles.map((role) => ({
    value: role.value,
    label: role.label,
    icon: role.icon,
}));

const formatRoleOptionLabel = ({ label, icon }) => (
    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {icon}
        {label}
    </span>
);

const BroadcastMessage = () => {

    //#region redux
    const dispatch = useDispatch();

    const selectedTimeline = useSelector(selectSelectedTimeline);
    const allEmployees = useSelector(selectAllEmployees);
    const usedTheme = useSelector(selectUsedTheme);
    const sendBroadcastMessageStatus = useSelector(selectSendBroadcastMessageStatus);
    const firebaseUser = useSelector(selectFirebaseUser);
    const dbUser = useSelector(selectFirebaseInfo);                                       // logged in user
    //#endregion

    //#region local state
    const [searchStaff, setSearchStaff] = useState("");
    const [selectedRoleFilters, setSelectedRoleFilters] = useState([]);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [messageText, setMessageText] = useState("");
    const [description, setDescription] = useState("");
    //#endregion

    //#region derived
    // All online employees at the airport, regardless of selected timeline area
    const onlineEmployees = useMemo(() => {
        if (!Array.isArray(allEmployees)) return [];
        return allEmployees.filter((employee) => employee?.status !== "offline");
    }, [allEmployees]);

    // Filter online employees by any selected roles (empty filter => all roles)
    const filteredOnlineEmployees = useMemo(() => {
        if (!Array.isFullArray(selectedRoleFilters)) return onlineEmployees;
        const selectedRoleValues = selectedRoleFilters.map((r) => r.value);
        return onlineEmployees.filter((employee) => selectedRoleValues.includes(employee?.role));
    }, [onlineEmployees, selectedRoleFilters]);
    //#endregion

    //#region use effects
    useEffect(() => {
        dispatch(resetSendBroadcastMessageStatus());
    }, []);

    useEffect(() => {
        if (sendBroadcastMessageStatus === "succeeded" || sendBroadcastMessageStatus === "failed") {
            dispatch(resetSendBroadcastMessageStatus());
            if (sendBroadcastMessageStatus === "succeeded") {
                dispatch(setToast({
                    message: "Broadcast message sent successfully will be delivered to the employees shortly",
                    statusType: "success",
                }));
                dispatch(setShowToast(true));
                setMessageText("");
                setDescription("");
                setSelectedEmployees([]);
                dispatch(setOpenBroadcastMessages(false));
            }
        }
    }, [sendBroadcastMessageStatus]);
    //#endregion

    //#region functions

    // format employees for select dropdown (respects the role filter)
    const getEmployeeOptions = () => {
        if (!filteredOnlineEmployees) return [];

        return filteredOnlineEmployees.map(employee => {
            const capitalizedName = employee.title
                ? employee.title.charAt(0).toUpperCase() + employee.title.slice(1)
                : employee.title;
            return {
                value: employee,
                label: `${capitalizedName} - ${employee.role || employee.employeeId}`
            };
        });
    };

    // get selected employee option for dropdown
    const getSelectedEmployeeOption = () => {
        if (!Array.isFullArray(selectedEmployees)) return [];
        else return selectedEmployees;
    };

    // handle employee selection from dropdown
    const handleEmployeeChange = (selectedOptions) => {
        setSelectedEmployees(selectedOptions);
    };

    const handleSendBroadcastMessage = () => {
        dispatch(sendBroadcastMessage({
            iata: dbUser?.details?.authority,
            body: {
                senderId: firebaseUser?.uid,
                senderName: firebaseUser?.displayName,
                text: messageText,
                receiversIds: selectedEmployees.map(employee => employee.value.employeeId),
                source: "PORTAL",
                description: description,
                area: selectedTimeline ? {
                    id: selectedTimeline?._id,
                    name: selectedTimeline?.title
                }
                    : null
            }
        }));
    };

    const handleSwitchSearchStaff = (value) => {
        setSearchStaff(value);
        if (value === "all") {
            setSelectedEmployees(getEmployeeOptions());
        } else if (value === "selectEmployees") {
            setSelectedEmployees([]);
        }
    };

    // When role filter changes, keep selections in sync with the filtered list
    const handleRoleFilterChange = (newRoleFilters) => {
        const nextFilters = newRoleFilters || [];
        setSelectedRoleFilters(nextFilters);

        const nextRoleValues = nextFilters.map((r) => r.value);
        const matchesRoleFilter = (employee) =>
            nextRoleValues.length === 0 || nextRoleValues.includes(employee?.role);

        if (searchStaff === "all") {
            // Rebuild the full audience from the freshly filtered online employees
            const nextOptions = (onlineEmployees || [])
                .filter(matchesRoleFilter)
                .map((employee) => {
                    const capitalizedName = employee.title
                        ? employee.title.charAt(0).toUpperCase() + employee.title.slice(1)
                        : employee.title;
                    return {
                        value: employee,
                        label: `${capitalizedName} - ${employee.role || employee.employeeId}`,
                    };
                });
            setSelectedEmployees(nextOptions);
        } else if (searchStaff === "selectEmployees") {
            // Drop any currently selected employees that no longer match the role filter
            setSelectedEmployees((prev) =>
                (prev || []).filter((option) => matchesRoleFilter(option?.value))
            );
        }
    };

    //#endregion

    const isSending = sendBroadcastMessageStatus === "loading";
    const canSend = !!messageText && selectedEmployees.length > 0 && !isSending;

    return (
        <div style={{ padding: Spacing.SCALE_12 }}>

            {/* header */}
            <div style={{ marginBottom: Spacing.SCALE_22 }}>
                <FiRadio size={28} color={Colors.PRM_LIGHT_BLUE} />
                <h5 style={{ fontWeight: 'bold', marginTop: '8px', marginBottom: '4px' }}>Broadcast Message</h5>
                <p style={{ fontSize: '14px', color: 'grey', margin: 0 }}>
                    Send a message to online employees at your airport
                </p>
            </div>

            <CustomDivider noMargin={true} />

            {/* audience selector */}
            <div className='mt-3'>
                <label style={labelStyle}>
                    <FiUsers size={14} /> Audience
                </label>
                <Row className='g-2'>
                    <Col xs={6}>
                        <div
                            onClick={() => handleSwitchSearchStaff("all")}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '10px 12px',
                                border: `1px solid ${searchStaff === 'all' ? Colors.PRM_LIGHT_BLUE : usedTheme.BORDER_COLOR}`,
                                backgroundColor: searchStaff === 'all' ? `${Colors.PRM_LIGHT_BLUE}15` : 'transparent',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease-in-out',
                            }}
                        >
                            <Form.Check
                                type="radio"
                                checked={searchStaff === "all"}
                                onChange={() => handleSwitchSearchStaff("all")}
                                style={{ margin: 0 }}
                            />
                            <FiUsers size={16} color={searchStaff === 'all' ? Colors.PRM_LIGHT_BLUE : 'grey'} />
                            <span style={{ fontSize: '14px', fontWeight: '500' }}>All Employees</span>
                        </div>
                    </Col>
                    <Col xs={6}>
                        <div
                            onClick={() => handleSwitchSearchStaff("selectEmployees")}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '10px 12px',
                                border: `1px solid ${searchStaff === 'selectEmployees' ? Colors.PRM_LIGHT_BLUE : usedTheme.BORDER_COLOR}`,
                                backgroundColor: searchStaff === 'selectEmployees' ? `${Colors.PRM_LIGHT_BLUE}15` : 'transparent',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease-in-out',
                            }}
                        >
                            <Form.Check
                                type="radio"
                                checked={searchStaff === "selectEmployees"}
                                onChange={() => handleSwitchSearchStaff("selectEmployees")}
                                style={{ margin: 0 }}
                            />
                            <FiUserCheck size={16} color={searchStaff === 'selectEmployees' ? Colors.PRM_LIGHT_BLUE : 'grey'} />
                            <span style={{ fontSize: '14px', fontWeight: '500' }}>Select Employees</span>
                        </div>
                    </Col>
                </Row>
            </div>

            {/* role filter (only meaningful once an audience mode is chosen) */}
            {searchStaff && (
                <div className='mt-3'>
                    <label style={labelStyle}>
                        <FiFilter size={14} /> Filter by Role <span style={{ color: 'grey', fontWeight: 400 }}>(optional)</span>
                    </label>
                    <Select
                        options={roleFilterOptions}
                        value={selectedRoleFilters}
                        onChange={handleRoleFilterChange}
                        formatOptionLabel={formatRoleOptionLabel}
                        placeholder="All roles"
                        isClearable={true}
                        isMulti={true}
                        closeMenuOnSelect={false}
                        styles={{
                            control: (provided) => ({ ...provided, backgroundColor: usedTheme.BACKGROUND_COLOR, borderColor: usedTheme.BORDER_COLOR, color: usedTheme.TEXT_COLOR, minHeight: '38px' }),
                            placeholder: (provided) => ({ ...provided, color: usedTheme.TEXT_COLOR }),
                            singleValue: (provided) => ({ ...provided, color: usedTheme.TEXT_COLOR }),
                            multiValue: (provided) => ({ ...provided, backgroundColor: usedTheme.BORDER_COLOR }),
                            multiValueLabel: (provided) => ({ ...provided, color: usedTheme.TEXT_COLOR }),
                            multiValueRemove: (provided) => ({ ...provided, color: usedTheme.TEXT_COLOR, ':hover': { backgroundColor: usedTheme.BORDER_COLOR, color: usedTheme.TEXT_COLOR } }),
                            menu: (provided) => ({ ...provided, zIndex: 4, backgroundColor: usedTheme.BACKGROUND_COLOR, borderColor: usedTheme.BORDER_COLOR, color: usedTheme.TEXT_COLOR }),
                            option: (provided, state) => ({ ...provided, backgroundColor: state.isFocused ? usedTheme.BORDER_COLOR : usedTheme.BACKGROUND_COLOR, color: usedTheme.TEXT_COLOR, cursor: 'pointer' }),
                        }}
                    />
                </div>
            )}

            {/* audience details */}
            {searchStaff === "all" && (() => {
                const hasRoleFilter = Array.isFullArray(selectedRoleFilters);
                const roleLabel = hasRoleFilter
                    ? selectedRoleFilters.map((r) => r.label).join(', ')
                    : '';
                const count = filteredOnlineEmployees.length;
                return (
                    <div
                        style={{
                            marginTop: Spacing.SCALE_12,
                            padding: '10px 12px',
                            backgroundColor: `${Colors.PRM_LIGHT_BLUE}10`,
                            border: `1px solid ${Colors.PRM_LIGHT_BLUE}30`,
                            borderRadius: '6px',
                            fontSize: '13px',
                            color: 'grey',
                        }}
                    >
                        {hasRoleFilter
                            ? `All online ${roleLabel} at your airport will receive this message (${count} currently online).`
                            : `All online employees at your airport will receive this message${count ? ` (${count} currently online)` : ''}.`}
                    </div>
                );
            })()}

            {searchStaff === "selectEmployees" && (
                <div className='mt-3'>
                    <label style={labelStyle}>
                        <FiUserCheck size={14} /> Select Employees *
                    </label>
                    <Select
                        options={getEmployeeOptions()}
                        value={getSelectedEmployeeOption()}
                        onChange={handleEmployeeChange}
                        placeholder="Choose an employee..."
                        isClearable={true}
                        isMulti={true}
                        closeMenuOnSelect={false}
                        styles={{
                            control: (provided) => ({ ...provided, backgroundColor: usedTheme.BACKGROUND_COLOR, borderColor: usedTheme.BORDER_COLOR, color: usedTheme.TEXT_COLOR, minHeight: '38px', maxHeight: '38px', overflow: 'hidden' }),
                            valueContainer: (provided) => ({ ...provided, flexWrap: 'nowrap', overflow: 'hidden' }),
                            placeholder: (provided) => ({ ...provided, color: usedTheme.TEXT_COLOR }),
                            singleValue: (provided) => ({ ...provided, color: usedTheme.TEXT_COLOR }),
                            multiValue: (provided) => ({ ...provided, backgroundColor: usedTheme.BORDER_COLOR }),
                            multiValueLabel: (provided) => ({ ...provided, color: usedTheme.TEXT_COLOR }),
                            multiValueRemove: (provided) => ({ ...provided, color: usedTheme.TEXT_COLOR, ':hover': { backgroundColor: usedTheme.BORDER_COLOR, color: usedTheme.TEXT_COLOR } }),
                            menu: (provided) => ({ ...provided, zIndex: 4, backgroundColor: usedTheme.BACKGROUND_COLOR, borderColor: usedTheme.BORDER_COLOR, color: usedTheme.TEXT_COLOR }),
                            option: (provided, state) => ({ ...provided, backgroundColor: state.isFocused ? usedTheme.BORDER_COLOR : usedTheme.BACKGROUND_COLOR, color: usedTheme.TEXT_COLOR, cursor: 'pointer' }),
                        }}
                    />
                </div>
            )}

            {/* description */}
            <div className='mt-3'>
                <label style={labelStyle}>
                    <FiFileText size={14} /> Description <span style={{ color: 'grey', fontWeight: 400 }}>(optional)</span>
                </label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    onChange={(e) => setDescription(e.target.value)}
                    value={description}
                    placeholder="Enter broadcast message description..."
                    style={{ resize: "none" }}
                />
            </div>

            {/* message */}
            <div className='mt-3'>
                <label style={labelStyle}>
                    <FiMessageSquare size={14} /> Message *
                </label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    onChange={(e) => setMessageText(e.target.value)}
                    value={messageText}
                    style={{ resize: "none" }}
                    placeholder="Enter message text..."
                />
            </div>

            {/* send button */}
            <div className='mt-3' style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="primary"
                    onClick={handleSendBroadcastMessage}
                    disabled={!canSend}
                    style={{
                        backgroundColor: canSend ? Colors.PRM_LIGHT_BLUE : undefined,
                        borderColor: canSend ? Colors.PRM_LIGHT_BLUE : undefined,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        minWidth: '160px',
                        fontWeight: 500,
                    }}
                >
                    {isSending ? (
                        <>
                            <CustomSpinner size={"small"} />
                            <span>Sending...</span>
                        </>
                    ) : (
                        <>
                            <IoMdSend size={18} />
                            <span>Send Broadcast</span>
                        </>
                    )}
                </Button>
            </div>

        </div>
    );
};

export default BroadcastMessage;
